import React, { useEffect, useMemo, useState } from 'react';
import { Popup, Icon, Loader, Header, Button } from 'semantic-ui-react';
import {
  ContractDocusignSendHistoryDto,
  ContractListDto,
  contractService,
  LatestDocusignSendSummaryDto,
} from '../../services/contracts/ContractService';
import { getContractTypeLabel } from '../../utils/contractGroupVtmeOrder';
import { resolveDocusignHistoryIconColor } from '../../utils/docusignSendHistoryUtils';
import DocusignHistoryEntry from './DocusignHistoryEntry';
import DocusignSendHistoryModal from './DocusignSendHistoryModal';
import GroupDocusignSendHistoryModal from './GroupDocusignSendHistoryModal';

const DOCUSIGN_HISTORY_ICON_STYLE: React.CSSProperties = {
  cursor: 'pointer',
  marginLeft: '8px',
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  background: 'transparent',
};

const POPOVER_ICON = 'docusign-history-flat-icon';

interface DocusignSendHistoryPopoverProps {
  contractId: string;
  latestSend?: LatestDocusignSendSummaryDto | null;
  groupContracts?: ContractListDto[];
  historyTitleSuffix?: string;
  trigger?: React.ReactNode;
}

const DocusignSendHistoryPopover: React.FC<DocusignSendHistoryPopoverProps> = ({
  contractId,
  latestSend,
  groupContracts,
  historyTitleSuffix,
  trigger,
}) => {
  const [history, setHistory] = useState<ContractDocusignSendHistoryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFullModal, setShowFullModal] = useState(false);
  const isGroupHistory = Boolean(groupContracts?.length);
  const iconColor = resolveDocusignHistoryIconColor(latestSend, groupContracts);

  const contractLabelById = useMemo(() => {
    const map = new Map<string, string>();
    groupContracts?.forEach((contract) => {
      map.set(contract.id, getContractTypeLabel(contract));
    });
    return map;
  }, [groupContracts]);

  const shouldShowFullHistoryButton = isGroupHistory || history.length > 1;

  useEffect(() => {
    setHistory([]);
    setError(null);
  }, [contractId, latestSend?.requestedAt, latestSend?.status, latestSend?.isProcessing, groupContracts]);

  const loadHistory = async () => {
    if (history.length > 0) return;
    setLoading(true);
    setError(null);
    try {
      if (isGroupHistory && groupContracts) {
        const batches = await Promise.all(
          groupContracts.map((contract) => contractService.getDocusignSendHistory(contract.id))
        );
        const merged = batches
          .flat()
          .sort(
            (a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
          );
        setHistory(merged);
      } else {
        const data = await contractService.getDocusignSendHistory(contractId);
        setHistory(data);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar histórico DocuSign';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const previewEntry = history[0];

  const resolvedTitleSuffix = useMemo(() => {
    if (previewEntry) {
      const labelFromEntry = contractLabelById.get(previewEntry.contractId);
      if (labelFromEntry) return ` - ${labelFromEntry}`;
    }
    return historyTitleSuffix ?? '';
  }, [previewEntry, contractLabelById, historyTitleSuffix]);

  return (
    <>
      <style>{`
        .docusign-history-icon-trigger.icon,
        .docusign-history-popup-root .icon,
        .docusign-history-flat-icon.icon {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
          background: transparent !important;
        }
        .docusign-history-icon-trigger.icon:before,
        .docusign-history-popup-root .icon:before,
        .docusign-history-flat-icon.icon:before {
          border: none !important;
          box-shadow: none !important;
        }
        .docusign-history-popup-root .ui.header > .icon {
          border: none !important;
          background: transparent !important;
        }
      `}</style>
      <Popup
        trigger={
          trigger || (
            <Icon
              name="file alternate"
              className="docusign-history-icon-trigger"
              style={DOCUSIGN_HISTORY_ICON_STYLE}
              color={iconColor}
              size="small"
              title={
                iconColor === 'grey'
                  ? 'Sem histórico de envio DocuSign'
                  : 'Histórico DocuSign'
              }
            />
          )
        }
        flowing
        hoverable
        on="hover"
        onOpen={loadHistory}
        position="bottom center"
        wide="very"
      >
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Loader active inline size="small" />
            <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
              Carregando histórico DocuSign...
            </div>
          </div>
        ) : error ? (
          <div style={{ padding: '10px', maxWidth: '300px' }}>
            <Icon name="warning circle" color="red" className={POPOVER_ICON} />
            <span style={{ color: '#666' }}>{error}</span>
          </div>
        ) : history.length === 0 ? (
          <div style={{ padding: '10px', maxWidth: '300px' }}>
            <Icon name="info circle" color="grey" className={POPOVER_ICON} />
            <span style={{ color: '#666' }}>Nenhum envio DocuSign registrado</span>
            {isGroupHistory && (
              <Button
                size="mini"
                fluid
                basic
                color="blue"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullModal(true);
                }}
                style={{ marginTop: '12px' }}
              >
                <Icon name="list" />
                Ver histórico completo do grupo
              </Button>
            )}
          </div>
        ) : (
          <div className="docusign-history-popup-root" style={{ maxWidth: '400px', padding: '10px' }}>
            <Header size="tiny" style={{ marginBottom: '12px' }}>
              <Icon name="file alternate" className={POPOVER_ICON} />
              Histórico DocuSign{resolvedTitleSuffix}
            </Header>

            {previewEntry && (
              <DocusignHistoryEntry item={previewEntry} index={0} isLast />
            )}

            {shouldShowFullHistoryButton && (
              <Button
                size="mini"
                fluid
                basic
                color="blue"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullModal(true);
                }}
                style={{ marginTop: '12px' }}
              >
                <Icon name="list" />
                {isGroupHistory
                  ? 'Ver histórico completo do grupo'
                  : `Ver histórico completo (${history.length} envios)`}
              </Button>
            )}
          </div>
        )}
      </Popup>

      {showFullModal && isGroupHistory && groupContracts ? (
        <GroupDocusignSendHistoryModal
          isOpen={showFullModal}
          onClose={() => setShowFullModal(false)}
          groupContracts={groupContracts}
        />
      ) : showFullModal ? (
        <DocusignSendHistoryModal
          isOpen={showFullModal}
          onClose={() => setShowFullModal(false)}
          history={history}
          contractId={contractId}
        />
      ) : null}
    </>
  );
};

export default DocusignSendHistoryPopover;
